import ParametersIcon from '@mui/icons-material/FormatListBulleted';
import { Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { customFieldStyles } from '../../assets/jss/material-dashboard-pro-react/views/customFieldStyles';
import customFieldsMappings from '../../constants/customFieldsMappings';
import { PromptAlert } from '../../features/ux/RouterPrompt';
import Card from '../Card/Card';
import CardBody from '../Card/CardBody';
import CardHeader from '../Card/CardHeader';
import CardIcon from '../Card/CardIcon';
import Button from '../CustomButtons/Button';
import CustomAutocomplete from '../CustomInput/CustomAutocomplete';
import CustomInput from '../CustomInput/CustomInput';
import GridContainer from '../Grid/GridContainer';
import GridItem from '../Grid/GridItem';
import { useGetTenantQuery } from 'api/tenant.api';
import { useRetrieveCTProjectTypesQuery } from 'api/tenant.api';
import { useUpdateTenantCustomFieldsMutation } from 'api/tenant.api';

const useStyles = makeStyles(customFieldStyles);

export default function CustomFieldsTab({
  onSuccessTab,
  tab,
  setDirtyTabStatus,
  tenantStatus,
}) {
  const classes = useStyles();

  const {data: tenant} = useGetTenantQuery();
  const {data: tenantCTProjectTypes, isFetching} = useRetrieveCTProjectTypesQuery()
  const retrievingCTProjectValues = isFetching;
  const ctConfig = tenant?.ct_config ?? {};
  const [updateCustomFields] = useUpdateTenantCustomFieldsMutation();

  const [viewIsDirty, setViewIsDirty] = useState(false);
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [warningAlertOpts, setWarningAlertOpts] = useState({
    title: '',
    message: '',
    okText: '',
    showCancel: false,
    openedBy: '',
  });

  const [barcodeArray, setBarcodeArray] = React.useState('');
  const [barcodeArrayState, setBarcodeArrayState] = React.useState('');
  const [receiptText, setReceiptText] = React.useState('');
  const [receiptTextState, setReceiptTextState] = React.useState('');

  const [formState, setFormState] = useState({});
  const [errors, setErrors] = useState({});
  const [searchFilters, setSearchFilters] = useState({
    type: '',
    name: '',
    typeKey: '',
    value: '',
  });

  const [selectedOptions, setSelectedOptions] = useState({});
  const [errorNoProjectKeys, setErrorNoProjectKeys] = useState(false);
  const [errorNoProjectKeysMsg, setErrorNoProjectKeysMsg] = useState(null);

  const [saving, setSaving] = useState(false);

  const BARCODE_REGEX = /^[A-Za-z0-9_-]+$/;

  useEffect(() => {
    if (setDirtyTabStatus) {
      setDirtyTabStatus(viewIsDirty);
    }
  }, [viewIsDirty]);

  useEffect(() => {
    const {
      order_type,
      lineitem_type,
      customer_type,
      payment_type,
      transaction_type,
    } = ctConfig;

    const missingFields = [];

    if (!order_type || order_type === 'None') missingFields.push('Order Type');
    if (!lineitem_type || lineitem_type === 'None')
      missingFields.push('Lineitem Type');
    if (!customer_type || customer_type === 'None')
      missingFields.push('Customer Type');
    if (!payment_type || payment_type === 'None')
      missingFields.push('Payment Type');
    if (!transaction_type || transaction_type === 'None')
      missingFields.push('Transaction Type');

    if (missingFields.length > 0) {
      setErrorNoProjectKeys(true);
      setErrorNoProjectKeysMsg(
        <>
          <div id={'title'}>
            <Icon>info</Icon> <b>Type Matches Not Found:</b>
          </div>
          <br />
          Please complete and save the following project settings for this
          Environment: Missing
          <ul>
            {missingFields.map((field, index) => (
              <li key={index}>{field}</li>
            ))}
          </ul>{' '}
          specified on the Project Settings tab
        </>
      );
    } else {
      setErrorNoProjectKeysMsg(null);
    }
  }, [ctConfig, setErrorNoProjectKeysMsg]);

  useEffect(() => {
    const initialState = {};
    setBarcodeArray(ctConfig?.product_barcode_scan_attribute);
    setReceiptText(ctConfig?.product_receipt_text_attribute);
    customFieldsMappings.forEach(({ tenantField }) => {
      initialState[tenantField] = ctConfig[tenantField] || 'None';
    });
    setFormState(initialState);
  }, [ctConfig]);

  const handleFieldChange = (tenantField, value) => {
    const field = customFieldsMappings.find(
      (f) => f.tenantField === tenantField
    );

    if (value === 'None' && field.required) {
      setErrors((prev) => ({
        ...prev,
        [tenantField]: `This field is required.`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, [tenantField]: '' }));
    }

    setFormState((prev) => ({
      ...prev,
      [tenantField]: value,
    }));

    setSelectedOptions((prev) => {
      const updated = { ...prev };

      if (value === 'None' || value === '') {
        delete updated[tenantField];
      } else {
        updated[tenantField] = value;
      }

      return updated;
    });

    setViewIsDirty(true);
  };

  const getOptionsByType = useCallback(
    (typeKey) => {

      const staticOptions = customFieldsMappings.find(
        (field) => field.typeKey === typeKey && field.staticOptions
      )?.staticOptions || [];

      const tenantType = tenantCTProjectTypes?.find((type) =>
        customFieldsMappings.some(
          (field) => field.key === type.key && field.typeKey === typeKey
        )
      );

      if (!tenantType) {
        return [];
      }

      const availableOptions = tenantType.fieldDefinitions.map((field) => ({
        label: field.label['en-US'] || field.name,
        value: field.name,
      }));

      const selectedForType = Object.keys(selectedOptions)
        .filter((key) => {
          const field = customFieldsMappings.find((f) => f.tenantField === key);
          return field && field.typeKey === typeKey;
        })
        .map((key) => selectedOptions[key]);

      return [...availableOptions
        .filter((option) => !selectedForType.includes(option.value)),
        ...(staticOptions || [])]
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    [tenantCTProjectTypes, selectedOptions, customFieldsMappings]
  );

  const handleSearchChange = (key, value) => {
    setSearchFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filteredMappings = useCallback(() => {
    return customFieldsMappings
      .filter((field) => {
        const formValue = formState[field.tenantField] || '';

        return (
          field.type.toLowerCase().includes(searchFilters.type.toLowerCase()) &&
          field.name.toLowerCase().includes(searchFilters.name.toLowerCase()) &&
          field.typeKey
            .toLowerCase()
            .includes(searchFilters.typeKey.toLowerCase()) &&
          formValue.toLowerCase().includes(searchFilters.value.toLowerCase())
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [searchFilters, formState]);

  const getUniqueValues = (key) => {
    return [...new Set(customFieldsMappings.map((field) => field[key]))];
  };

  const alertIsDirty = () => {
    setWarningAlertOpts({
      title: 'Discard Changes?',
      message:
        'You have unsaved changes. Are you sure you want to discard them?',
      showCancel: true,
      openedBy: 'revert',
    });
    setShowWarningAlert(true);
  };

  const revertForm = () => {
    setFormState(ctConfig);
    setBarcodeArray(ctConfig?.product_barcode_scan_attribute ?? '');
    setReceiptText(ctConfig?.product_receipt_text_attribute ?? '');
    setViewIsDirty(false);
    setShowWarningAlert(false);
  };

  const validateRequiredFields = () => {
    const validation = [];
    const newErrors = {};

    customFieldsMappings.forEach((field) => {
      const fieldValue = formState[field.tenantField];

      if (field.required && (!fieldValue || fieldValue === 'None')) {
        validation.push({ name: `${field.name}`, validation: 'is REQUIRED' });
        newErrors[field.tenantField] = 'This field is REQUIRED';
      } else {
        newErrors[field.tenantField] = '';
      }
    });

    const isValidBarcode = BARCODE_REGEX.test(barcodeArray ?? '');
    const isBarcodeCorrectLength =
      barcodeArray?.length >= 2 && barcodeArray?.length <= 256;

    if (!barcodeArray || !isValidBarcode || !isBarcodeCorrectLength) {
      validation.unshift({
        name: 'Barcode Array',
        validation: !barcodeArray ? 'is REQUIRED' : 'is INVALID',
      });
      newErrors['barcodeArray'] =
        'This field is ' + (!barcodeArray ? 'REQUIRED' : 'INVALID');
    } else {
      newErrors['barcodeArray'] = false;
    }

    const isReceiptTextCorrectLength =
      receiptText?.length >= 2 && receiptText?.length <= 256;

    if (!receiptText || !isReceiptTextCorrectLength) {
      validation.unshift({
        name: 'Receipt Text',
        validation: !receiptText ? 'is REQUIRED' : 'is INVALID',
      });
      newErrors['receiptText'] =
        'This field is ' + (!receiptText ? 'REQUIRED' : 'INVALID');
    } else {
      newErrors['receiptText'] = false;
    }

    setErrors(newErrors);

    return validation;
  };

  const getMessageForValidationWarn = (missingFields) => {
    return (
      <div>
        The following Required attributes are missing, a Custom Field value:
        <div className={classes.fieldContainerWarnErrors}>
          {missingFields.map((field) => (
            <div>
              <b>{field.name}</b> {field.validation}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const saveChanges = async () => {
    if (tenantStatus === 'active') {
      const missingFields = validateRequiredFields();

      if (missingFields.length) {
        setWarningAlertOpts({
          title: 'Unable to Save Changes',
          message: getMessageForValidationWarn(missingFields),
          openedBy: 'validation',
          okText: 'OK',
        });
        setShowWarningAlert(true);
        return;
      }
    }

    setSaving(true);

    const newCtConfig = {
      ...formState,
      product_barcode_scan_attribute: barcodeArray,
      product_receipt_text_attribute: receiptText,
    };

    try {
        await updateCustomFields({
          ctConfig: newCtConfig,
          tenantId: tenant._id,
    }).unwrap();

      setViewIsDirty(false);

      if (onSuccessTab) {
        onSuccessTab(tab.name);
      }

      toast.success('Changes saved successfully', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });
    } catch (error) {
      toast.error('An error occurred while ' + 'trying to save the changes', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const hasErrors = useCallback(() => {
    return Object.values(errors).some((error) => error);
  }, [errors]);

  return (
    <>
      {showWarningAlert && (
        <PromptAlert
          message={warningAlertOpts?.message}
          title={warningAlertOpts?.title}
          showCancel={warningAlertOpts?.showCancel ?? false}
          okText={warningAlertOpts?.okText}
          onConfirm={() => {
            if (warningAlertOpts?.openedBy === 'revert') {
              revertForm();
            }
            setShowWarningAlert(false);
          }}
          onCancel={() => setShowWarningAlert(false)}
        />
      )}
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card className={classes.productAttributesCard}>
            <CardHeader color='primary' icon>
              <CardIcon color='primary'>
                <ParametersIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Product Attributes</h4>
            </CardHeader>
            <CardBody>
              <GridContainer>
                <GridItem xs={8}>
                  <CustomInput
                    helperText={
                      errors['barcodeArray'] ??
                      'Min length 2, Max length 256, Pattern: ^[A-Za-z0-9_-]+$'
                    }
                    success={barcodeArrayState === 'success'}
                    error={errors['barcodeArray']}
                    labelText='Barcode Array'
                    id='label'
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      disabled: errorNoProjectKeys,
                      value: barcodeArray ?? '',
                      onChange: (event) => {
                        setBarcodeArray(event.target.value);
                        setViewIsDirty(true);
                      },
                      autoComplete: 'off',
                      error: errors['barcodeArray'],
                    }}
                  />
                  <CustomInput
                    helperText={
                      errors?.['receiptText'] ?? 'Min length 2, Max length 256'
                    }
                    success={receiptTextState === 'success'}
                    error={errors?.['receiptText']}
                    labelText='Receipt Text'
                    id='label'
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      disabled: errorNoProjectKeys,
                      value: receiptText ?? '',
                      onChange: (event) => {
                        setReceiptText(event.target.value);
                        setViewIsDirty(true);
                      },
                      autoComplete: 'off',
                      error: errors['receiptText'],
                    }}
                  />
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem xs={8} />
                <GridItem xs={4}>
                  <Button
                    color='rose'
                    onClick={saveChanges}
                    className={classes.registerButton}
                    disabled={
                      !viewIsDirty ||
                      errorNoProjectKeys ||
                      saving ||
                      hasErrors()
                    }
                  >
                    {saving ? (
                      <>
                        <CircularProgress
                          sx={{ color: 'white', marginRight: 1 }}
                          size={20}
                        />{' '}
                        saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                  <Button
                    color='secondary'
                    onClick={alertIsDirty}
                    className={classes.registerButton}
                    disabled={!viewIsDirty || errorNoProjectKeys || saving}
                  >
                    Cancel
                  </Button>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={12} md={12}>
          <Card className={classes.customFieldsCard}>
            <CardHeader color='primary' icon>
              <CardIcon color='primary'>
                <ParametersIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                Required Attribute - Custom Field Mappings
              </h4>
            </CardHeader>
            <CardBody className={classes.customFieldsCardBody}>
              {errorNoProjectKeys && (
                <div className={classes.errorNoProjectKeysMsg}>
                  {errorNoProjectKeysMsg}
                </div>
              )}

              {!errorNoProjectKeys && (
                <>
                  <GridContainer>
                    <GridItem xs={3}>
                      <p className={classes.tableHeaderText}>Type</p>
                      <CustomInput
                        labelText={`Search ${
                          getUniqueValues('type').length
                        } records...`}
                        variant='outlined'
                        fullWidth
                        inputProps={{
                          onChange: (e) =>
                            handleSearchChange('type', e.target.value),
                        }}
                      />
                    </GridItem>
                    <GridItem xs={3}>
                      <p className={classes.tableHeaderText}>
                        Required Attribute
                      </p>
                      <CustomInput
                        labelText={`Search ${customFieldsMappings.length} records...`}
                        variant='outlined'
                        fullWidth
                        inputProps={{
                          onChange: (e) =>
                            handleSearchChange('name', e.target.value),
                        }}
                      />
                    </GridItem>
                    <GridItem xs={3}>
                      <p className={classes.tableHeaderText}>Type Key</p>
                      <CustomInput
                        labelText={`Search ${
                          getUniqueValues('typeKey').length
                        } records...`}
                        variant='outlined'
                        fullWidth
                        inputProps={{
                          onChange: (e) =>
                            handleSearchChange('typeKey', e.target.value),
                        }}
                      />
                    </GridItem>
                    <GridItem xs={3}>
                      <p className={classes.tableHeaderText}>Custom Field</p>
                      <CustomInput
                        labelText='Search in values...'
                        variant='outlined'
                        fullWidth
                        inputProps={{
                          onChange: (e) =>
                            handleSearchChange('value', e.target.value),
                        }}
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer>
                    {filteredMappings().map((field) => (
                      <GridItem xs={12} key={field.tenantField}>
                        <GridContainer alignItems='center'>
                          <GridItem xs={3}>
                            <p>{field.type}</p>
                          </GridItem>
                          <GridItem xs={3}>
                            <p>
                              <b>{field.name}</b>
                            </p>
                          </GridItem>
                          <GridItem xs={3}>
                            <p>{field.typeKey}</p>
                          </GridItem>
                          <GridItem xs={3}>
                          <CustomInput
                              labelText="Custom Field value"
                              id={field.tenantField}
                              formControlProps={{
                                fullWidth: true,
                                disabled: retrievingCTProjectValues,
                              }}
                              inputProps={{
                                value: formState[field.tenantField] || "",
                                onChange: (e) =>
                                  handleFieldChange(
                                    field.tenantField,
                                    e.target.value
                                  ),
                                onKeyUp: (event) => {
                                  if (
                                    event.key === "Backspace" &&
                                    event.target.value === ""
                                  ) {
                                    handleFieldChange(field.tenantField, "");
                                  }
                                },
                              }}
                              error={!!errors[field.tenantField]}
                              helperText={errors[field.tenantField]}
                            />
                          </GridItem>
                        </GridContainer>
                      </GridItem>
                    ))}

                    {filteredMappings().length === 0 && (
                      <Box className={classes.noResultsBox}>
                        No results, try a different search term
                      </Box>
                    )}
                  </GridContainer>
                </>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </>
  );
}
